import React, { Suspense } from "react";
import {
    BrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigationType,
} from "react-router-dom";
import {
    BOOKING_INVITATION_ROUTE,
    NOTIFICATION_SETTINGS_ROUTE,
    TERMS_OF_USE,
    PRIVACY_POLICY,
    IDENTIFICATION_CALLBACK,
    IDENTIFICATION_FAILED,
    INVALID_PASSWORD_LINK,
    LOGIN,
    LOGIN_ABSOLUTE,
    LOGIN_SUPPORT,
    MEETING_COST_CALCULATOR,
    MEETING_COST_CALCULATOR_MEETING_ROUTE,
    NOTIFICATION_SETTINGS_HELP,
    PASSWORD,
    REQUEST_PASSWORD_RESET,
    RESET_PASSWORD,
    SET_PASSWORD,
    STRONG_IDENTIFICATION,
} from "urls";
import { ACTIVE_AUTH_USER_STORAGE_KEY } from "Entity/ActiveUser";
import { useStoredState } from "Utils/storedState";
import { PublicPageLoading } from "components/common/PageTemplates/PublicPageLoading";
import { AppErrorFallback } from "components/common/AppErrorFallback";
import {
    ACTIVE_NORRE_USER_STORAGE_KEY,
    ActiveNorreUser,
} from "Entity/ActiveUser/NorreUser";
import * as Sentry from "@sentry/react";
import { api_base, isDevEnv, isProdEnv } from "Utils/requests";
import { componentLoader } from "Utils/componentLoader";
import { MeQuery } from "Apollo/Queries/UserQueries/index.generated";

const LoginRequiredRoutes = React.lazy(() =>
    componentLoader(() => import("LoginRequiredRoutes")),
);

const LoginStrongIdentificationView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/LoginViews/StrongIdentification/StrongIdentificationView"),
    ),
);
const LoginIdentificationFailedCallbackView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/LoginViews/StrongIdentification/IdentificationFailedCallbackView"),
    ),
);
const LoginIdentificationCallbackView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/LoginViews/StrongIdentification/IdentificationCallbackView"),
    ),
);
const LoginSetPasswordView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/LoginViews/PasswordViews/SetPasswordView"),
    ),
);
const LoginRequestPasswordResetView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/LoginViews/PasswordViews/RequestPasswordResetView"),
    ),
);
const LoginResetPasswordView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/LoginViews/PasswordViews/ResetPasswordView"),
    ),
);
const LoginInvalidLinkView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/LoginViews/PasswordViews/InvalidLinkView"),
    ),
);
const LoginPage = React.lazy(() =>
    componentLoader(
        () => import("components/views/LoginViews/LoginPage/LoginPage"),
    ),
);
const EmailNotificationSettingsHelpView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/PublicViews/EmailNotificationSettingsHelpView"),
    ),
);

const LoginSupportView = React.lazy(() =>
    componentLoader(
        () => import("components/views/LoginViews/LoginSupportView"),
    ),
);
const BookingInvitationView = React.lazy(() =>
    componentLoader(
        () => import("components/views/PublicViews/BookingInvitationView"),
    ),
);
const NotificationSettingsByTokenView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/PublicViews/NotificationSettingsByTokenView"),
    ),
);
const TermsOfUseView = React.lazy(() =>
    componentLoader(
        () => import("components/views/PublicViews/TermsOfUseView"),
    ),
);
const PrivacyPolicyView = React.lazy(() =>
    componentLoader(
        () => import("components/views/PublicViews/PrivacyPolicyView"),
    ),
);
const MeetingCostCalculatorView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/PublicViews/MeetingCostCalculator/MeetingCostCalculatorView"),
    ),
);
const MeetingCostCalculatorMeetingView = React.lazy(() =>
    componentLoader(
        () =>
            import("components/views/PublicViews/MeetingCostCalculator/MeetingCostCalculatorMeetingView"),
    ),
);

Sentry.init({
    dsn: "https://236cd6f9e59a9cc0b9c6990e5f3aef99@o4507192475320320.ingest.de.sentry.io/4507214576287824",
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration(),
    ],
    enabled: !isDevEnv(),
    environment: isProdEnv() ? "prod" : "test",
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [api_base],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const AppRouter = () => {
    const [activeAuthUser, setActiveAuthUser] = useStoredState<
        MeQuery["me"] | null
    >(ACTIVE_AUTH_USER_STORAGE_KEY, null);

    const [activeNorreUser, setActiveNorreUser] =
        useStoredState<ActiveNorreUser>(ACTIVE_NORRE_USER_STORAGE_KEY, null);

    return (
        <BrowserRouter
            future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
            }}
        >
            <Sentry.ErrorBoundary
                fallback={({ error, resetError }) => (
                    <AppErrorFallback error={error} resetError={resetError} />
                )}
            >
                <SentryRoutes>
                    <Route
                        path={STRONG_IDENTIFICATION}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <LoginStrongIdentificationView />
                            </Suspense>
                        }
                    />
                    <Route
                        path={IDENTIFICATION_FAILED}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <LoginIdentificationFailedCallbackView />
                            </Suspense>
                        }
                    />
                    <Route
                        path={IDENTIFICATION_CALLBACK}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <LoginIdentificationCallbackView
                                    setActiveAuthUser={setActiveAuthUser}
                                    setActiveNorreUser={setActiveNorreUser}
                                />
                            </Suspense>
                        }
                    />
                    <Route path={PASSWORD}>
                        <Route
                            path={SET_PASSWORD}
                            element={
                                <Suspense fallback={<PublicPageLoading />}>
                                    <LoginSetPasswordView />
                                </Suspense>
                            }
                        />
                        <Route
                            path={REQUEST_PASSWORD_RESET}
                            element={
                                <Suspense fallback={<PublicPageLoading />}>
                                    <LoginRequestPasswordResetView />
                                </Suspense>
                            }
                        />
                        <Route
                            path={RESET_PASSWORD}
                            element={
                                <Suspense fallback={<PublicPageLoading />}>
                                    <LoginResetPasswordView />
                                </Suspense>
                            }
                        />
                        <Route
                            path={INVALID_PASSWORD_LINK}
                            element={
                                <Suspense fallback={<PublicPageLoading />}>
                                    <LoginInvalidLinkView />
                                </Suspense>
                            }
                        />
                    </Route>
                    <Route
                        path={LOGIN}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <LoginPage
                                    setActiveAuthUser={setActiveAuthUser}
                                    setActiveNorreUser={setActiveNorreUser}
                                />
                            </Suspense>
                        }
                    />
                    <Route
                        path={LOGIN_SUPPORT}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <LoginSupportView />
                            </Suspense>
                        }
                    />
                    <Route
                        path={NOTIFICATION_SETTINGS_HELP}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <EmailNotificationSettingsHelpView />
                            </Suspense>
                        }
                    />
                    <Route
                        path={BOOKING_INVITATION_ROUTE}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <BookingInvitationView />
                            </Suspense>
                        }
                    />
                    <Route
                        path={NOTIFICATION_SETTINGS_ROUTE}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <NotificationSettingsByTokenView />
                            </Suspense>
                        }
                    />
                    <Route
                        path={TERMS_OF_USE}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <TermsOfUseView />
                            </Suspense>
                        }
                    />
                    <Route
                        path={PRIVACY_POLICY}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <PrivacyPolicyView />
                            </Suspense>
                        }
                    />
                    <Route
                        path={MEETING_COST_CALCULATOR}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <MeetingCostCalculatorView />
                            </Suspense>
                        }
                    />
                    <Route
                        path={MEETING_COST_CALCULATOR_MEETING_ROUTE}
                        element={
                            <Suspense fallback={<PublicPageLoading />}>
                                <MeetingCostCalculatorMeetingView />
                            </Suspense>
                        }
                    />
                    <Route
                        path="auth"
                        element={<Navigate to={LOGIN_ABSOLUTE} replace />}
                    />
                    <Route
                        path="*"
                        element={
                            <Sentry.ErrorBoundary
                                fallback={({ error, resetError }) => (
                                    <AppErrorFallback
                                        error={error}
                                        resetError={resetError}
                                        loginRequiredArea
                                    />
                                )}
                            >
                                <Suspense fallback={<PublicPageLoading />}>
                                    <LoginRequiredRoutes
                                        activeAuthUser={activeAuthUser}
                                        setActiveAuthUser={setActiveAuthUser}
                                        activeNorreUser={activeNorreUser}
                                        setActiveNorreUser={setActiveNorreUser}
                                    />
                                </Suspense>
                            </Sentry.ErrorBoundary>
                        }
                    />
                </SentryRoutes>
            </Sentry.ErrorBoundary>
        </BrowserRouter>
    );
};
